import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Info,
  Download,
  Printer,
  ExternalLink,
  Plus,
  Trash2,
} from "lucide-react";
import { Modal, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import AppLoader from "../../components/AppLoader/appLoader";
import "./OrderDetails.css";
import { useOrderDetails } from "../../customHooks/Orders/useOrderDetails";
import { useSidebarNavigation } from "../../customHooks/SidebarNavigation/useSidebarNavigation";
import { Tooltip as BSTooltip } from "bootstrap";
import { useNavigate } from "react-router-dom";
import CopyIcon from "../../components/CopyIcon/CopyIcon";
import {
  formatDate,
  formatDateTime,
} from "../../components/Constants/Constants";
import moment from "moment";
import { useToasts } from "react-toast-notifications";
import { useDispatch, useSelector } from "react-redux";


// Helper function to convert MongoDB Decimal128 and other formats to number
const formatPrice = (value) => {
  if (value === null || value === undefined) return "0.00";
  let numericValue;
  if (typeof value === "number") {
    numericValue = value;
  } else if (typeof value === "string") {
    numericValue = parseFloat(value) || 0;
  } else if (typeof value === "object" && value.$numberDecimal) {
    numericValue = parseFloat(value.$numberDecimal) || 0;
  } else {
    return "0.00";
  }
  return numericValue.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatData = (StringtoDays) => {
  const duration = moment.duration(StringtoDays);
  return duration.asDays();
};

const OrderDetails = ({ orderId }) => {
  // 1. Data Fetching
  const {
    orderData,
    loading,
    error,
    handleGoBack,
    handleRaiseRecon,
    raisingRecon,
    handleLatestSettlement,
    sendingSettlement,
    handleRaiseIssue,
    isRaiseModalOpen,
    openRaiseModal,
    closeRaiseModal,
    raiseIssueFormData,
    setRaiseIssueFormData,
    handleCategoryChange,
    categoryMapping,
    subCategoryOptions,
    referenceType,
    handleRaiseIssueSubmit,
    isSubmitting,
    handleRefChange,
    addRefRow,
    removeRefRow,
    selectedResolutions,
    handleResolutionSelect,
    handleResolutionSubmit,
    isResolutionSubmitting,
    handleRequestLatestStatus,
    handleRaiseSettlementRecon,
    settlementLoading,
    reconLoading,
    handleRaiseReport,
  } = useOrderDetails({
    orderId,
  });
  const {hasService} = useSidebarNavigation();

  const navigate = useNavigate();

  // Get storeId from Redux to detect store changes
  const { storeId } = useSelector((state) => state.loginSlice);
  const previousStoreId = React.useRef(storeId);

  const formatLabel = (text) =>
    text?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const formatText = (text) =>
    text?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.charAt(0).toUpperCase() + c.slice(1));

  const handleFormChange = (e) => {
    const { id, value } = e.target;
    setRaiseIssueFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      setRaiseIssueFormData((prev) => ({
        ...prev,
        images: Array.from(e.target.files),
      }));
    }
  };

  const getStatusBadgeStyles = (status) => {
    const s = status.toLowerCase();
    switch (true) {
      case s === "completed" || s.includes("delivered"):
        return "bg-success-subtle text-success border-success-subtle";
      case s === "cancelled" || s.includes("returned"):
        return "bg-danger-subtle text-danger border-danger-subtle";
      case s === "accepted":
        return "bg-primary-subtle text-primary border-primary-subtle";
      case s === "in-progress":
        return "bg-info-subtle text-info border-info-subtle";
      case s === "created":
        return "bg-secondary-subtle text-secondary border-secondary-subtle";
      default:
        return "bg-warning-subtle text-warning border-warning-subtle";
    }
  };

  // 2. State Management
  const [showSections, setShowSections] = useState({
    summary: true,
    history: true,
    buyerApp: false,
    shipping: true,
    recon: true,
    issues: true,
    settlements: true,
  });

  // Reconciliation State
  const [activeReconTab, setActiveReconTab] = useState("raisedByMe");
  const [expandedReconId, setExpandedReconId] = useState(null);

  // Issues State
  const [activeIssueTab, setActiveIssueTab] = useState("byMe");
  const [activeIssueSubTab, setActiveIssueSubTab] = useState("active");
  const [expandedIssueId, setExpandedIssueId] = useState(null);

  // 3. Derived Data Variables
  const settlement = orderData?.settlementDetails || {};
  const igmData = orderData?.igmData || {};
  const reconsRaised = settlement.recons_raised || [];
  const reconsReceived = settlement.recons_recieved || [];
  const currentReconData =
    activeReconTab === "raisedByMe" ? reconsRaised : reconsReceived;

  // IGM (Issue) Logic
  const reported = igmData.ReportedIssues || {
    issues: [],
    active_issues_count: 0,
    closed_issues_count: 0,
    total_issues_count: 0,
  };
  const counterParty = igmData.CounterPartyIssues || {
    issues: [],
    active_issues_count: 0,
    closed_issues_count: 0,
    total_issues_count: 0,
  };
  const currentCategory = activeIssueTab === "byMe" ? reported : counterParty;

  const filteredIssues =
    currentCategory.issues?.filter((issue) =>
      activeIssueSubTab === "active"
        ? issue.issue_status !== "CLOSED"
        : issue.issue_status === "CLOSED",
    ) || [];

  const pricing = orderData?.orderValues?.pricing || {};
  const breakdown = pricing.breakdown || {};
  const igmSummary = orderData?.orderValues?.getIssuesSummary || {};
  const orderSummary = orderData?.orderSummary || {};
  const orderDates = orderData?.orderValues?.dates || {};


  // 4. Effects
  useEffect(() => {
    // Auto-expand first recon item if available
    if (currentReconData.length > 0) {
      setExpandedReconId(currentReconData[0].recon_id);
    } else {
      setExpandedReconId(null);
    }
  }, [activeReconTab, currentReconData.length]);

  useEffect(() => {
    if (orderData) {
      const tooltipTriggerList = [].slice.call(
        document.querySelectorAll('[data-bs-toggle="tooltip"]'),
      );

      const tooltipList = tooltipTriggerList.map((el) => {
        // Use the renamed BSTooltip here
        return new BSTooltip(el, {
          trigger: "hover focus",
          delay: { show: 100, hide: 100 },
          container: "body",
        });
      });

      return () => tooltipList.forEach((t) => t.dispose());
    }
  }, [orderData, activeReconTab, expandedReconId, activeIssueSubTab]);

  // 5. Handlers
  const toggleSection = (section) =>
    setShowSections((prev) => ({ ...prev, [section]: !prev[section] }));
  const toggleReconItem = (id) =>
    setExpandedReconId(expandedReconId === id ? null : id);
  const toggleIssueItem = (id) =>
    setExpandedIssueId(expandedIssueId === id ? null : id);

  if (loading) return <AppLoader />;
  if (error) return <div className="alert alert-danger m-4">{error}</div>;
  if (!orderData) return null;

  

  return (
    <div className="order-details-wrapper">
      <div className="container-fluid">
        {/* --- HEADER --- */}
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-body p-4">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <button
                  onClick={handleGoBack}
                  className="btn btn-light border btn-sm p-2 rounded-circle"
                >
                  <ArrowLeft size={20} className="text-secondary" />
                </button>
                <div>
                  <h4 className="fw-bold mb-1 text-dark">Order Details</h4>
                  <div className="d-flex align-items-center gap-2 text-muted small">
                    <span>
                      ID:{" "}
                      <span className="font-monospace text-dark">
                        {orderData.id}
                      </span>
                    </span>
                    <span className="text-secondary">|</span>
                    <span>Shopify: {orderData.seller_order_name || "N/A"}</span>
                  </div>
                </div>
              </div>

              <div className="d-flex flex-column align-items-end">
                <div className="d-flex gap-2 mb-2">
                  <div>
                    STATUS:
                    <span className="badge bg-light text-dark border px-3 py-2 mx-2">
                      {orderData.orderstatus?.toUpperCase()}
                    </span>
                  </div>
                  {hasService(5) && (
                  <div>
                    SETTLEMENT:
                    <span
                      className={`badge px-3 py-2 mx-2 ${
                        orderData.settlement_status === "SETTLED"
                          ? "bg-success-subtle text-success border border-success-subtle"
                          : "bg-warning-subtle text-warning-emphasis border border-warning-subtle"
                      }`}
                    >
                      {orderData.settlement_status || "PENDING"}
                    </span>
                  </div>
                  )}
                </div>
                <div className="extra-small text-muted">
                  Created: {new Date(orderData.created_at).toLocaleString()}
                </div>
              </div>
            </div>

            {/* --- METRICS ROW --- */}
            <div className="row g-3 mt-4 row-cols-1 row-cols-sm-2 row-cols-md-4 row-cols-lg-7 justify-content-center">
              {/* 1. Order Amount */}
              <div className="col">
                <div className="metric-card accent-success">
                  <div className="text-overline">ORDER AMOUNT</div>
                  <div className="metric-value">
                    ₹{formatPrice(orderData.order_amount)}
                  </div>
                  <div className="mt-2 pt-2 border-top extra-small">
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">Delivery</span>
                      <span className="text-success">
                        +{orderSummary.deliveryCharges || 0}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">Refund</span>
                      <span className="text-danger">
                        -{orderSummary.totalReturnPrice || 0}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">Buyer Finder Fee</span>
                      <span className="text-danger">
                        -{orderSummary.finderFeeAmount || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 2. Expected Settlement */}
              <div className="col">
                <div className="metric-card accent-primary">
                  <div className="text-overline">EXP. SETTLEMENT</div>
                  <div className="metric-value text-primary-theme">
                    ₹{formatPrice(pricing.buyerPayable)}
                  </div>
                  <div className="extra-small text-muted mt-2">
                    Net Receivable
                  </div>
                </div>
              </div>

              {/* 3. Settlement Status */}
              {hasService(5) && (
              <div className="col">
                <div className="metric-card accent-info">
                  <div className="text-overline">SETTLEMENT STATUS</div>
                  <div className="d-flex flex-column gap-1 mt-1">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="extra-small text-muted">
                        Settlement Received
                      </span>
                      <span className="fw-bold text-success small">
                        ₹
                        {formatPrice(
                          settlement.settlement_summary?.settled_amount,
                        )}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="extra-small text-muted">
                        Settlement Pending
                      </span>
                      <span className="fw-    bold text-danger small">
                        ₹
                        {formatPrice(
                          settlement.settlement_summary?.amount_to_be_settle,
                        )}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="extra-small text-muted">
                        Settlement Basis
                      </span>
                      <span className="fw-bold text-success extra-small settlement-basis-value">
                        {formatText(orderData?.payment_details?.settlement_basis) || "N/A"}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="extra-small text-muted">
                        Settlement Window
                      </span>
                      <span className="fw-bold text-success small">
                        {(() => {
                          const days = formatData(
                            orderData?.payment_details?.settlement_window,
                          );
                          return days
                            ? `${days} ${days === 1 ? "Day" : "Days"}`
                            : "N/A";
                        })()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* 4. Reconciliation */}
              {hasService(5) && (
              <div className="col">
                <div className="metric-card accent-warning">
                  <div className="text-overline">RECONCILIATION</div>
                  <div className="metric-value">
                    {settlement.recon_count || 0}
                  </div>
                  <div className="extra-small text-muted mt-1">
                    {settlement.recon_count > 0 && (
                      <>{settlement.recon_count} Active Cases</>
                    )}
                  </div>
                </div>
              </div>
              )}

              {/* 5. Timeline */}
              <div className="col">
                <div className="metric-card accent-purple">
                  <div className="text-overline">TIMELINE</div>
                  <div className="extra-small">
                    <div className="mb-1">
                      <span className="fw-bold">Order Date:</span>{" "}
                      {formatDateTime(orderDates.orderDate) || "N/A"}
                    </div>
                    <div>
                      {orderDates.returnWindowEnd && (
                        <>
                          <span className="fw-bold">Return Expiry:</span>{" "}
                          {formatDateTime(orderDates.returnWindowEnd) || "N/A"}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* 7. Issues For Me */}
              {hasService(6) && (
              <div className="col">
                <div className="metric-card accent-secondary">
                  <div className="text-overline">COUNTERPARTY ISSUES</div>
                  <div className="metric-value">
                    {igmSummary.issuesForMe?.total || 0}
                  </div>
                  <div className="extra-small text-muted mt-1">
                    {igmSummary.issuesForMe?.total > 0 && (
                      <>
                        {igmSummary.issuesForMe?.active || 0} Active /{" "}
                        {igmSummary.issuesForMe?.closed || 0} Closed
                      </>
                    )}
                  </div>
                </div>
              </div>
              )}

              {/* 6. Issues By Me */}
              {hasService(6) && (
              <div className="col">
                <div className="metric-card accent-danger">
                  <div className="text-overline">REPORTED ISSUES</div>
                  <div className="metric-value text-danger">
                    {igmSummary.issuesByMe?.total || 0}
                  </div>
                  {igmSummary.issuesByMe?.total > 0 && (
                    <div className="extra-small text-muted mt-1">
                      {igmSummary.issuesByMe?.active || 0} Active /{" "}
                      {igmSummary.issuesByMe?.closed || 0} Closed
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-lg-8">
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center bg-white py-3">
                <h6 className="mb-0 fw-bold text-dark">Order Items</h6>
              </div>
              <div className="table-responsive">
                <table className="table mb-0 align-middle">
                  <thead className="bg-light">
                    <tr>
                      <th style={{ width: "45%" }} className="text-center ps-4">
                        Product Name
                      </th>
                      <th style={{ width: "10%" }} className="text-center">
                        Qty
                      </th>
                      <th style={{ width: "15%" }} className="text-center">
                        Original Price
                      </th>
                      <th style={{ width: "15%" }} className="text-center">
                        Current Status
                      </th>
                      <th style={{ width: "15%" }} className="text-center pe-4">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderData.orderFulfilments?.items?.map((item, i) => {
                      const deliveryTimeline = item.fulfilmentTimeline?.delivery
                        ?.map((e) => {
                          const parts = e.time.split(" ");
                          const datePart = parts[0]; // e.g., 2026-01-16
                          const timePart = parts[1]?.substring(0, 5) || ""; // e.g., 16:31

                          return `${datePart} ${timePart} - ${e.status.replace(
                            /-/g,
                            " ",
                          )}`;
                        })
                        .join("\n");

                      const cancelledTimeline =
                        item.fulfilmentTimeline?.cancelled
                          ?.map((e) => {
                            const parts = e.time.split(" ");
                            const datePart = parts[0]; // e.g., 2026-01-16
                            const timePart = parts[1]?.substring(0, 5) || ""; // e.g., 16:31

                            return `${datePart} ${timePart} - ${e.status.replace(
                              /-/g,
                              " ",
                            )}`;
                          })
                          .join("\n");

                      const returnTimeline = item.fulfilmentTimeline?.return
                        ?.map((e) => {
                          const parts = e.time.split(" ");
                          const datePart = parts[0];
                          const timePart = parts[1]?.substring(0, 5) || "";

                          const formattedStatus = e.status
                            .replace(/_/g, " ")
                            .replace(/-/g, " ");

                          return `${datePart} ${timePart} - ${formattedStatus}`;
                        })
                        .join("\n");

                      const hasReturned = item.statusSummary?.returnedQty > 0;
                      const hasCancelled = item.statusSummary?.cancelledQty > 0;
                      const currentItemStatus = item.status || "N/A";
                      // debug: log item.status from API for this item
                      // //console.log('OrderDetails: item.status for index', i, '=>', item.status);

                      return (
                        <tr key={i}>
                          <td className="text-center ps-4">
                            <div
                              className="copy-cell d-inline-flex align-items-center justify-content-center py-2"
                              style={{ width: "fit-content" }}
                            >
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id={`tooltip-product-${i}`}>
                                    {item.productName}
                                  </Tooltip>
                                }
                              >
                                <div
                                  className="text-dark text-truncate small fw-bold"
                                  style={{
                                    maxWidth: "200px",
                                    cursor: "default",
                                    fontSize: "0.85rem",
                                    textAlign: "left",
                                  }}
                                >
                                  {item.productName}
                                </div>
                              </OverlayTrigger>
                              <div className="ms-1">
                                <CopyIcon textToCopy={item.productName} />
                              </div>
                            </div>
                          </td>

                          <td className="text-center fw-medium">
                            {item.statusSummary?.deliveredQty || 0}
                          </td>

                          <td className="text-center">
                            ₹{formatPrice(item.pricing?.unitPrice)}
                          </td>

                          <td className="text-center">
                            <div className="status-cell-wrapper d-flex flex-column gap-1 align-items-center">
                              <div className="d-flex align-items-center gap-1">
                                <span
                                  className={`badge status-badge-fixed rounded-pill border ${getStatusBadgeStyles(
                                    currentItemStatus || "N/A",
                                  )}`}
                                >
                                  {(currentItemStatus || "N/A")
                                    .replace(/-/g, " ")
                                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                                </span>

                                {deliveryTimeline && (
                                  <span
                                    className="timeline-info-btn"
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="left"
                                    style={{ whiteSpace: "pre-line" }}
                                    title={deliveryTimeline}
                                  >
                                    <Info size={12} strokeWidth={2.5} />
                                  </span>
                                )}
                              </div>

                              {hasReturned && (
                                <div className="d-flex align-items-center gap-1">
                                  <span className="badge status-badge-fixed rounded-pill bg-danger-subtle text-danger border border-danger-subtle">
                                    Returned ({item.statusSummary.returnedQty})
                                  </span>

                                  {returnTimeline && (
                                    <span
                                      className="timeline-info-btn text-danger"
                                      data-bs-toggle="tooltip"
                                      data-bs-placement="left"
                                      style={{ whiteSpace: "pre-line" }}
                                      title={returnTimeline}
                                    >
                                      <Info size={12} strokeWidth={2.5} />
                                    </span>
                                  )}
                                </div>
                              )}

                              {hasCancelled && (
                                <div className="d-flex align-items-center gap-1">
                                  <span className="badge status-badge-fixed rounded-pill bg-danger-subtle text-danger border border-danger-subtle">
                                    Cancelled ({item.statusSummary.cancelledQty}
                                    )
                                  </span>

                                  {cancelledTimeline && (
                                    <span
                                      className="timeline-info-btn text-danger"
                                      data-bs-toggle="tooltip"
                                      data-bs-placement="left"
                                      style={{ whiteSpace: "pre-line" }}
                                      title={
                                        item.fulfilmentTimeline?.cancelled?.[0]
                                          ?.message
                                      }
                                    >
                                      <Info size={12} strokeWidth={2.5} />
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>

                          <td className="text-center pe-4 fw-bold">
                            <div>
                              ₹{formatPrice(item.pricing?.deliveredTotal)}
                            </div>
                            {hasReturned && (
                              <div className="text-danger small fw-normal">
                                -₹{formatPrice(item.pricing?.returnedTotal)}
                              </div>
                            )}

                            {hasCancelled && (
                              <div className="text-danger small fw-normal">
                                -₹{formatPrice(item.pricing?.cancelledTotal)}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-light">
                    <tr>
                      <td
                        colSpan="4"
                        className="text-end text-muted fw-bold small py-3 bg-light"
                      >
                        Total Line Item Amount:
                      </td>
                      <td className="text-center pe-4 fw-bold py-3 text-dark">
                        ₹{breakdown.subtotal?.toFixed(2) || "0.00"}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

          
            {/* --- 2. SETTLEMENT BREAKDOWN UPDATED WITH ROW ACTIONS --- */}
              {hasService(5) && (
            <div className="card mb-4">
              <div
                className="card-header d-flex justify-content-between align-items-center cursor-pointer"
                onClick={() => toggleSection("settlements")}
              >
                <h6 className="mb-0 fw-bold text-dark">Settlement Breakdown</h6>
                <div className="d-flex align-items-center gap-3">
                  {/* Keeping Global Send Settlement button if applicable */}
                  {orderData?.settlementDetails?.isSettleBtnUnable && (
                    <button
                      className="btn btn-success btn-sm extra-small fw-bold"
                      onClick={handleLatestSettlement}
                    >
                      Send Settlement
                    </button>
                  )}
                  {showSections.settlements ? (
                    <ChevronUp size={18} />
                  ) : (
                    <ChevronDown size={18} />
                  )}
                </div>
              </div>

              {showSections.settlements && (
                <div className="table-responsive">
                  {orderData.settlementDetails?.settlements?.length > 0 ? (
                    <table className="table mb-0 align-middle">
                      <thead className="bg-light">
                        <tr>
                          <th className="text-center ps-3 small fw-bold text-uppercase">
                            Settlement ID
                          </th>
                          <th className="text-center small fw-bold text-uppercase">
                            Requested Settlement
                          </th>
                          <th className="text-center small fw-bold text-uppercase">
                            Settled Amount
                          </th>
                          <th className="text-center small fw-bold text-uppercase">
                            Pending Amount
                          </th>
                          <th className="text-center small fw-bold text-uppercase">
                            Status
                          </th>
                          <th className="text-center small fw-bold text-uppercase">
                            Requested Date
                          </th>
                          <th className="text-center small fw-bold text-uppercase">
                            Errors
                          </th>
                          <th className="text-center pe-3 small fw-bold text-uppercase">
                            Recon ID
                          </th>
                          <th className="text-center pe-3 small fw-bold text-uppercase">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderData.settlementDetails?.settlements?.map(
                          (s, i) => {
                            // Filter out null errors and get unique messages
                            const errorMessages =
                              s.errors?.filter((err) => err !== null) || [];

                            return (
                              <tr key={i} className="text-center small">
                                <td className="ps-3">
                                  <div className="copy-cell d-inline-flex align-items-center justify-content-center gap-2">
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={
                                        <Tooltip id={`settlement-id-${i}`}>
                                          {s.settlement_id}
                                        </Tooltip>
                                      }
                                    >
                                      <span
                                        className="truncate-cell font-monospace text-primary fw-bold"
                                        style={{ maxWidth: "150px" }}
                                      >
                                        {s.settlement_id}
                                      </span>
                                    </OverlayTrigger>
                                    <CopyIcon textToCopy={s.settlement_id} />
                                  </div>
                                </td>
                                <td className="fw-bold">
                                  ₹
                                  {formatPrice(
                                    s.requested_settlement_amount
                                      ?.$numberDecimal,
                                  )}
                                </td>
                                <td>₹{formatPrice(s.settled_amount)}</td>
                                <td className="fw-bold">
                                  ₹
                                  {formatPrice(
                                    s.pending_amount?.$numberDecimal,
                                  )}
                                </td>
                                <td>
                                  <span
                                    className={`badge rounded-pill ${
                                      s.requested_settlement_status ===
                                      "SETTLED"
                                        ? "bg-success-subtle text-success"
                                        : "bg-warning-subtle text-warning-emphasis"
                                    }`}
                                  >
                                    {s.requested_settlement_status?.replace(
                                      /_/g,
                                      " ",
                                    )}
                                  </span>
                                </td>
                                <td className="text-muted">
                                  {new Date(
                                    s.requested_date,
                                  ).toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  })}
                                </td>
                                <td>
                                  {/* Error Messages Display */}
                                  {errorMessages.length > 0 ? (
                                    <div className="d-flex flex-column gap-2">
                                      {errorMessages.map((err, idx) => (
                                        <span
                                          key={idx}
                                          className="badge bg-danger-subtle text-danger border border-danger-subtle"
                                          style={{
                                            fontSize: "0.7rem",
                                            display: "inline-block",
                                          }}
                                        >
                                          {err.message}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-muted">NA</span>
                                  )}
                                </td>
                                <td className="pe-3">
                                  {s.recons?.length > 0 ? (
                                    <div className="copy-cell d-inline-flex align-items-center justify-content-center gap-2">
                                      <OverlayTrigger
                                        placement="top"
                                        overlay={
                                          <Tooltip id={`recon-id-${i}`}>
                                            {s.recons[0]._id}
                                          </Tooltip>
                                        }
                                      >
                                        <span
                                          className="truncate-cell font-monospace text-dark fw-bold"
                                          style={{ maxWidth: "120px" }}
                                        >
                                          {s.recons[0]._id}
                                        </span>
                                      </OverlayTrigger>
                                      <CopyIcon textToCopy={s.recons[0]._id} />
                                    </div>
                                  ) : (
                                    <span className="text-muted">NA</span>
                                  )}
                                </td>
                                <td className="pe-3">
                                  <div className="d-flex flex-column gap-2">
                                    <button
                                      className="btn btn-primary btn-sm extra-small fw-bold"
                                      onClick={() =>
                                        handleRequestLatestStatus(
                                          s.settlement_mongo_id,
                                        )
                                      }
                                      // disabled={settlementLoading[s._id || s.settlement_id]}
                                      style={{
                                        whiteSpace: "nowrap",
                                        fontSize: "0.7rem",
                                      }}
                                    >
                                      {settlementLoading[s.settlement_mongo_id]
                                        ? "..."
                                        : "Request Latest Status"}
                                    </button>

                                    {errorMessages.length > 0 && (
                                      <button
                                        className="btn btn-warning btn-sm text-dark extra-small fw-bold"
                                        onClick={() =>
                                          handleRaiseSettlementRecon(
                                            s.settlement_id,
                                            i,
                                          )
                                        }
                                        disabled={reconLoading[s.settlement_id]}
                                        style={{
                                          whiteSpace: "nowrap",
                                          fontSize: "0.7rem",
                                        }}
                                      >
                                        {reconLoading[s.settlement_id]
                                          ? "..."
                                          : "+ Raise Reconciliation"}
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          },
                        )}
                      </tbody>
                    </table>
                  ) : (
                    <div className="text-center py-4 text-muted small border-top">
                      No settlement records found.
                    </div>
                  )}
                </div>
              )}
            </div>
           ) }

            {/* --- 3. RECONCILIATION UPDATED --- */}
              {hasService(5) && (
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-bold text-dark">Reconciliation</h6>
                <div className="d-flex align-items-center gap-3">
                  <button
                    className="btn btn-warning btn-sm text-dark extra-small fw-bold"
                    disabled={raisingRecon}
                    onClick={handleRaiseRecon}
                  >
                    {raisingRecon ? "Processing..." : "+ Raise Reconciliation"}
                  </button>
                </div>
              </div>
              <div className="card-body p-0">
                <ul className="nav custom-tabs border-bottom">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeReconTab === "raisedByMe" ? "active-tab" : ""
                      }`}
                      onClick={() => setActiveReconTab("raisedByMe")}
                    >
                      Raised By Me ({reconsRaised.length})
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeReconTab === "raisedForMe" ? "active-tab" : ""
                      }`}
                      onClick={() => setActiveReconTab("raisedForMe")}
                    >
                      Raised For Me ({reconsReceived.length})
                    </button>
                  </li>
                </ul>

                {currentReconData.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table mb-0 align-middle">
                      <thead className="bg-light">
                        <tr>
                          <th className="text-center ps-4 fw-bold">Recon ID</th>
                          <th className="text-center fw-bold">
                            Buyer App Name
                          </th>
                          <th className="text-center fw-bold">Status</th>
                          <th className="text-center fw-bold">Remarks</th>
                          <th className="text-center pe-4 fw-bold">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentReconData.map((recon) => {
                          const isExpanded = expandedReconId === recon.recon_id;
                          const buyerAppName =
                            recon.recon_messages?.[0]?.recon_sent?.sender ||
                            "NA";

                          const statusLabel = recon.status
                            ? recon.status.replace(/_/g, " ")
                            : "N/A";

                          const statusBgColor = statusLabel
                            .toLowerCase()
                            .includes("response")
                            ? "bg-warning-subtle text-warning-emphasis"
                            : statusLabel.toLowerCase().includes("accepted")
                              ? "bg-success-subtle text-success"
                              : "bg-danger-subtle text-danger";

                          return (
                            <React.Fragment key={recon.recon_id}>
                              <tr
                                className="cursor-pointer"
                                onClick={() => toggleReconItem(recon.recon_id)}
                              >
                                <td className="ps-4 font-monospace small fw-bold">
                                  {recon.recon_id}
                                </td>
                                <td className="text-center small">
                                  {formatLabel(buyerAppName)}
                                </td>
                                <td className="text-center">
                                  <span className={`badge ${statusBgColor}`}>
                                    {statusLabel}
                                  </span>
                                </td>
                                <td className="text-center">
                                  <div className="d-flex flex-wrap gap-1 justify-content-center">
                                    {recon.remarks &&
                                    recon.remarks.length > 0 ? (
                                      recon.remarks.map((r, idx) => (
                                        <span
                                          key={idx}
                                          className="badge bg-danger-subtle text-danger extra-small"
                                        >
                                          {typeof r === "string"
                                            ? r
                                            : r.message || JSON.stringify(r)}
                                        </span>
                                      ))
                                    ) : (
                                      <span className="text-muted extra-small">
                                        No remarks
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="text-center pe-4">
                                  <button
                                    className="btn btn-sm p-1"
                                    style={{
                                      border: "1px solid #e0e0e0",
                                      borderRadius: "50%",
                                      width: 32,
                                      height: 32,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      marginLeft: "auto",
                                      marginRight: "auto",
                                      backgroundColor: "transparent",
                                    }}
                                    onClick={() =>
                                      toggleReconItem(recon.recon_id)
                                    }
                                  >
                                    {isExpanded ? (
                                      <ChevronUp
                                        size={16}
                                        className="text-secondary"
                                      />
                                    ) : (
                                      <ChevronDown
                                        size={16}
                                        className="text-secondary"
                                      />
                                    )}
                                  </button>
                                </td>
                              </tr>

                              {isExpanded && (
                                <tr>
                                  <td
                                    colSpan="5"
                                    style={{
                                      padding: "20px 16px",
                                      backgroundColor: "#fff",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "block",
                                        width: "100%",
                                      }}
                                    >
                                      {/* Settlement Rows */}
                                      <div
                                        style={{
                                          width: "100%",
                                          overflowX: "auto",
                                          borderCollapse: "collapse",
                                        }}
                                      >
                                        <table
                                          style={{
                                            width: "100%",
                                            fontSize: "12px",
                                            borderCollapse: "collapse",
                                          }}
                                        >
                                          <thead>
                                            <tr
                                              style={{
                                                backgroundColor: "#f5f5f5",
                                                borderBottom:
                                                  "1px solid #d0d0d0",
                                              }}
                                            >
                                              <th
                                                style={{
                                                  padding: "10px 8px",
                                                  textAlign: "center",
                                                  fontSize: "10px",
                                                  fontWeight: 600,
                                                  textTransform: "uppercase",
                                                  letterSpacing: "0.3px",
                                                  color: "#666",
                                                  whiteSpace: "nowrap",
                                                  fontWeight: "bold",
                                                }}
                                              >
                                                Settlement ID
                                              </th>
                                              <th
                                                style={{
                                                  padding: "10px 8px",
                                                  textAlign: "center",
                                                  fontSize: "10px",
                                                  fontWeight: 600,
                                                  textTransform: "uppercase",
                                                  letterSpacing: "0.3px",
                                                  color: "#666",
                                                  whiteSpace: "nowrap",
                                                  fontWeight: "bold",
                                                }}
                                              >
                                                Payment ID
                                              </th>
                                              <th
                                                style={{
                                                  padding: "10px 8px",
                                                  textAlign: "center",
                                                  fontSize: "10px",
                                                  fontWeight: 600,
                                                  textTransform: "uppercase",
                                                  letterSpacing: "0.3px",
                                                  color: "#666",
                                                  whiteSpace: "nowrap",
                                                  fontWeight: "bold",
                                                }}
                                              >
                                                Status
                                              </th>
                                              <th
                                                style={{
                                                  padding: "10px 8px",
                                                  textAlign: "center",
                                                  fontSize: "10px",
                                                  fontWeight: 600,
                                                  textTransform: "uppercase",
                                                  letterSpacing: "0.3px",
                                                  color: "#666",
                                                  whiteSpace: "nowrap",
                                                  fontWeight: "bold",
                                                }}
                                              >
                                                Amount
                                                <br />
                                                (Expected Settlement + MSN
                                                Commission)
                                              </th>
                                              <th
                                                style={{
                                                  padding: "10px 8px",
                                                  textAlign: "center",
                                                  fontSize: "10px",
                                                  fontWeight: 600,
                                                  textTransform: "uppercase",
                                                  letterSpacing: "0.3px",
                                                  color: "#666",
                                                  whiteSpace: "nowrap",
                                                  fontWeight: "bold",
                                                }}
                                              >
                                                Buyer Finder Fee
                                              </th>
                                              <th
                                                style={{
                                                  padding: "10px 8px",
                                                  textAlign: "center",
                                                  fontSize: "10px",
                                                  fontWeight: 600,
                                                  textTransform: "uppercase",
                                                  letterSpacing: "0.3px",
                                                  color: "#666",
                                                  whiteSpace: "nowrap",
                                                  fontWeight: "bold",
                                                }}
                                              >
                                                TCS
                                              </th>
                                              <th
                                                style={{
                                                  padding: "10px 8px",
                                                  textAlign: "center",
                                                  fontSize: "10px",
                                                  fontWeight: 600,
                                                  textTransform: "uppercase",
                                                  letterSpacing: "0.3px",
                                                  color: "#666",
                                                  whiteSpace: "nowrap",
                                                  fontWeight: "bold",
                                                }}
                                              >
                                                TDS
                                              </th>
                                              <th
                                                style={{
                                                  padding: "10px 8px",
                                                  textAlign: "center",
                                                  fontSize: "10px",
                                                  fontWeight: 600,
                                                  textTransform: "uppercase",
                                                  letterSpacing: "0.3px",
                                                  color: "#666",
                                                  whiteSpace: "nowrap",
                                                  fontWeight: "bold",
                                                }}
                                              >
                                                Withholding
                                              </th>
                                              <th
                                                style={{
                                                  padding: "10px 8px",
                                                  textAlign: "center",
                                                  fontSize: "10px",
                                                  fontWeight: 600,
                                                  textTransform: "uppercase",
                                                  letterSpacing: "0.3px",
                                                  color: "#666",
                                                  whiteSpace: "nowrap",
                                                  fontWeight: "bold",
                                                }}
                                              >
                                                Settlement Ref No
                                              </th>
                                              <th
                                                style={{
                                                  padding: "10px 8px",
                                                  textAlign: "center",
                                                  fontSize: "10px",
                                                  fontWeight: 600,
                                                  textTransform: "uppercase",
                                                  letterSpacing: "0.3px",
                                                  color: "#666",
                                                  whiteSpace: "nowrap",
                                                  fontWeight: "bold",
                                                }}
                                              >
                                                Date
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {recon.recon_messages?.map(
                                              (msg, msgIdx) => {
                                                return (
                                                  <React.Fragment
                                                    key={msg.message_id}
                                                  >
                                                    {/* recon_sent settlements (RED) */}
                                                    {msg.recon_sent?.settlements?.map(
                                                      (set, sIdx) => (
                                                        <tr
                                                          key={`${msg.message_id}-sent-${sIdx}`}
                                                          style={{
                                                            backgroundColor:
                                                              "#ffcdd2",
                                                            borderBottom:
                                                              "1px solid #e0e0e0",
                                                          }}
                                                        >
                                                          <td
                                                            className="copy-cell"
                                                            style={{
                                                              padding:
                                                                "10px 8px",
                                                              fontSize: "12px",
                                                            }}
                                                          >
                                                            <OverlayTrigger
                                                              placement="top"
                                                              overlay={
                                                                <Tooltip
                                                                  id={`settlement-id-${msg.message_id}-${sIdx}`}
                                                                >
                                                                  {
                                                                    set.settlement_id
                                                                  }
                                                                </Tooltip>
                                                              }
                                                            >
                                                              <span
                                                                className="truncate-cell"
                                                                style={{
                                                                  fontFamily:
                                                                    "monospace",
                                                                }}
                                                              >
                                                                {
                                                                  set.settlement_id
                                                                }
                                                              </span>
                                                            </OverlayTrigger>
                                                            <CopyIcon
                                                              textToCopy={
                                                                set.settlement_id
                                                              }
                                                              className="recon-copy-icon"
                                                            />
                                                          </td>
                                                          <td
                                                            className="copy-cell"
                                                            style={{
                                                              padding:
                                                                "10px 8px",
                                                              fontSize: "12px",
                                                            }}
                                                          >
                                                            <OverlayTrigger
                                                              placement="top"
                                                              overlay={
                                                                <Tooltip
                                                                  id={`payment-id-${msg.message_id}-${sIdx}`}
                                                                >
                                                                  {
                                                                    set.payment_id
                                                                  }
                                                                </Tooltip>
                                                              }
                                                            >
                                                              <span className="truncate-cell">
                                                                {set.payment_id}
                                                              </span>
                                                            </OverlayTrigger>
                                                            <CopyIcon
                                                              textToCopy={
                                                                set.payment_id
                                                              }
                                                              className="recon-copy-icon"
                                                            />
                                                          </td>
                                                          <td
                                                            style={{
                                                              padding:
                                                                "10px 8px",
                                                              textAlign:
                                                                "center",
                                                            }}
                                                          >
                                                            <span
                                                              style={{
                                                                display:
                                                                  "inline-block",
                                                                backgroundColor:
                                                                  "#fff3cd",
                                                                color:
                                                                  "#856404",
                                                                padding:
                                                                  "3px 8px",
                                                                borderRadius:
                                                                  "3px",
                                                                fontSize:
                                                                  "11px",
                                                                fontWeight: 500,
                                                              }}
                                                            >
                                                              {(
                                                                set.status || ""
                                                              ).replace(
                                                                /_/g,
                                                                " ",
                                                              )}
                                                            </span>
                                                          </td>
                                                          <td
                                                            style={{
                                                              padding:
                                                                "10px 8px",
                                                              textAlign:
                                                                "center",
                                                              fontSize: "12px",
                                                              fontWeight: 500,
                                                            }}
                                                          >
                                                            <div>
                                                              ₹
                                                              {formatPrice(
                                                                set.amount,
                                                              )}
                                                            </div>
                                                            {recon.status === "ON RECON RECEIVED" && (
                                                              <div
                                                                style={{
                                                                  fontSize:
                                                                    "10px",
                                                                  color: "#666",
                                                                }}
                                                              >
                                                                {"("}₹
                                                                {formatPrice(
                                                                  pricing.buyerPayable,
                                                                )}{" "}
                                                                + ₹
                                                                {formatPrice(
                                                                  breakdown.msnFee,
                                                                )}
                                                                {")"}
                                                              </div>
                                                            )}
                                                          </td>
                                                          <td
                                                            style={{
                                                              padding:
                                                                "10px 8px",
                                                              textAlign:
                                                                "center",
                                                              fontSize: "12px",
                                                              color: "#d32f2f",
                                                            }}
                                                          >
                                                            {set.commission > 0 && "-"}
                                                            ₹
                                                            {formatPrice(
                                                              set.commission,
                                                            )}
                                                          </td>
                                                          <td
                                                            style={{
                                                              padding:
                                                                "10px 8px",
                                                              textAlign:
                                                                "center",
                                                              fontSize: "12px",
                                                            }}
                                                          >
                                                            ₹{set.tcs ?? "0.00"}
                                                          </td>
                                                          <td
                                                            style={{
                                                              padding:
                                                                "10px 8px",
                                                              textAlign:
                                                                "center",
                                                              fontSize: "12px",
                                                            }}
                                                          >
                                                            ₹{set.tds ?? "0.00"}
                                                          </td>
                                                          <td
                                                            style={{
                                                              padding:
                                                                "10px 8px",
                                                              textAlign:
                                                                "center",
                                                              fontSize: "12px",
                                                            }}
                                                          >
                                                            ₹
                                                            {set.withholding ??
                                                              "0.00"}
                                                          </td>
                                                          <td
                                                            style={{
                                                              padding:
                                                                "10px 8px",
                                                              textAlign:
                                                                "center",
                                                              fontSize: "12px",
                                                            }}
                                                          >
                                                            {set.ref_no || "NA"}
                                                          </td>
                                                          <td
                                                            style={{
                                                              padding:
                                                                "10px 8px",
                                                              textAlign:
                                                                "center",
                                                              fontSize: "12px",
                                                              color: "#999",
                                                            }}
                                                          >
                                                            {formatDateTime(
                                                              set.date,
                                                            )}
                                                          </td>
                                                        </tr>
                                                      ),
                                                    )}

                                                    {/* on_recon_received settlements (YELLOW) - if message_id matches */}
                                                    {msg.recon_sent
                                                      ?.message_id ===
                                                      msg.on_recon_received
                                                        ?.message_id &&
                                                      msg.on_recon_received?.settlements?.map(
                                                        (set, sIdx) => (
                                                          <tr
                                                            key={`${msg.message_id}-received-${sIdx}`}
                                                            style={{
                                                              backgroundColor:
                                                                "#fffacd",
                                                              borderBottom:
                                                                "1px solid #e0e0e0",
                                                            }}
                                                          >
                                                            <td
                                                              className="copy-cell"
                                                              style={{
                                                                padding:
                                                                  "10px 8px",
                                                                fontSize:
                                                                  "12px",
                                                              }}
                                                            >
                                                              <OverlayTrigger
                                                                placement="top"
                                                                overlay={
                                                                  <Tooltip
                                                                    id={`settlement-id-${msg.message_id}-received-${sIdx}`}
                                                                  >
                                                                    {
                                                                      set.settlement_id
                                                                    }
                                                                  </Tooltip>
                                                                }
                                                              >
                                                                <span
                                                                  className="truncate-cell"
                                                                  style={{
                                                                    fontFamily:
                                                                      "monospace",
                                                                  }}
                                                                >
                                                                  {
                                                                    set.settlement_id
                                                                  }
                                                                </span>
                                                              </OverlayTrigger>
                                                              <CopyIcon
                                                                textToCopy={
                                                                  set.settlement_id
                                                                }
                                                                className="recon-copy-icon"
                                                              />
                                                            </td>
                                                            <td
                                                              className="copy-cell"
                                                              style={{
                                                                padding:
                                                                  "10px 8px",
                                                                fontSize:
                                                                  "12px",
                                                              }}
                                                            >
                                                              <OverlayTrigger
                                                                placement="top"
                                                                overlay={
                                                                  <Tooltip
                                                                    id={`payment-id-${msg.message_id}-received-${sIdx}`}
                                                                  >
                                                                    {
                                                                      set.payment_id
                                                                    }
                                                                  </Tooltip>
                                                                }
                                                              >
                                                                <span className="truncate-cell">
                                                                  {
                                                                    set.payment_id
                                                                  }
                                                                </span>
                                                              </OverlayTrigger>
                                                              <CopyIcon
                                                                textToCopy={
                                                                  set.payment_id
                                                                }
                                                                className="recon-copy-icon"
                                                              />
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding:
                                                                  "10px 8px",
                                                                textAlign:
                                                                  "center",
                                                              }}
                                                            >
                                                              <span
                                                                style={{
                                                                  display:
                                                                    "inline-block",
                                                                  backgroundColor:
                                                                    "#fff3cd",
                                                                  color:
                                                                    "#856404",
                                                                  padding:
                                                                    "3px 8px",
                                                                  borderRadius:
                                                                    "3px",
                                                                  fontSize:
                                                                    "11px",
                                                                  fontWeight: 500,
                                                                }}
                                                              >
                                                                {(
                                                                  set.status ||
                                                                  ""
                                                                ).replace(
                                                                  /_/g,
                                                                  " ",
                                                                )}
                                                              </span>
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding:
                                                                  "10px 8px",
                                                                textAlign:
                                                                  "center",
                                                                fontSize:
                                                                  "12px",
                                                                fontWeight: 500,
                                                              }}
                                                            >
                                                              <div>
                                                                ₹
                                                                {formatPrice(
                                                                  set.amount,
                                                                )}
                                                              </div>
                                                              {(activeReconTab ===
                                                                "raisedForMe" || recon.status === "ON RECON RESPOND") && (
                                                                <div
                                                                  style={{
                                                                    fontSize:
                                                                      "10px",
                                                                    color:
                                                                      "#666",
                                                                  }}
                                                                >
                                                                  {"("}₹
                                                                  {formatPrice(
                                                                    pricing.buyerPayable,
                                                                  )}
                                                                  {" "}+ ₹
                                                                  {formatPrice(
                                                                    breakdown.msnFee,
                                                                  )}
                                                                  {")"}
                                                                </div>
                                                              )}
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding:
                                                                  "10px 8px",
                                                                textAlign:
                                                                  "center",
                                                                fontSize:
                                                                  "12px",
                                                                color:
                                                                  "#d32f2f",
                                                              }}
                                                            >
                                                              {set.commission > 0 && "-"}
                                                              ₹
                                                              {formatPrice(
                                                                set.commission,
                                                              )}
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding:
                                                                  "10px 8px",
                                                                textAlign:
                                                                  "center",
                                                                fontSize:
                                                                  "12px",
                                                              }}
                                                            >
                                                              ₹
                                                              {set.tcs ??
                                                                "0.00"}
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding:
                                                                  "10px 8px",
                                                                textAlign:
                                                                  "center",
                                                                fontSize:
                                                                  "12px",
                                                              }}
                                                            >
                                                              ₹
                                                              {set.tds ??
                                                                "0.00"}
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding:
                                                                  "10px 8px",
                                                                textAlign:
                                                                  "center",
                                                                fontSize:
                                                                  "12px",
                                                              }}
                                                            >
                                                              ₹
                                                              {set.withholding ??
                                                                "0.00"}
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding:
                                                                  "10px 8px",
                                                                textAlign:
                                                                  "center",
                                                                fontSize:
                                                                  "12px",
                                                              }}
                                                            >
                                                              {set.ref_no ||
                                                                "NA"}
                                                            </td>
                                                            <td
                                                              style={{
                                                                padding:
                                                                  "10px 8px",
                                                                textAlign:
                                                                  "center",
                                                                fontSize:
                                                                  "12px",
                                                                color: "#999",
                                                              }}
                                                            >
                                                              {formatDateTime(
                                                                set.date,
                                                              )}
                                                            </td>
                                                          </tr>
                                                        ),
                                                      )}
                                                  </React.Fragment>
                                                );
                                              },
                                            )}
                                          </tbody>
                                        </table>
                                      </div>

                                      {/* Action Buttons */}
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "flex-end",
                                          gap: "12px",
                                          padding: "16px",
                                          backgroundColor: "#fff",
                                          borderTop: "1px solid #e0e0e0",
                                        }}
                                      >
                                        {/* <button 
                                          className="btn btn-sm" 
                                          style={{ 
                                            border: "1px solid #ddd", 
                                            color: "#666",
                                            backgroundColor: "#fff",
                                            fontSize: "13px",
                                            padding: "6px 16px"
                                          }}
                                          onClick={() => handleRaiseRecon(recon.recon_id)}
                                        >
                                          ✓ Accept Recon
                                        </button> */}
                                        <button
                                          className="btn btn-sm btn-danger"
                                          style={{
                                            fontSize: "13px",
                                            padding: "6px 16px",
                                          }}
                                          onClick={openRaiseModal}
                                        >
                                          ⚠ Raise Issue
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted small">
                    No reconciliation records found.
                  </div>
                )}
              </div>
            </div>
              )}

            {/* 4. ISSUES LISTING */}
            {hasService(6) &&(
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-bold text-dark">Issue Listing</h6>

                {/* {orderData?.settlementDetails?.isIssueEnable && ( */}
                {activeIssueTab === "byMe" && (
                  <button
                    className="btn btn-danger btn-sm extra-small fw-bold"
                    onClick={openRaiseModal}
                  >
                    Raise Issue
                  </button>
                )}
                {/* // )} */}
              </div>

              <div className="card-body p-0">
                {/* Main Tabs */}
                <ul className="nav custom-tabs border-bottom">
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeIssueTab === "forMe" ? "active-tab" : ""
                      }`}
                      onClick={() => setActiveIssueTab("forMe")}
                    >
                      Counterparty Issues (
                      {counterParty.total_issues_count || 0})
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeIssueTab === "byMe" ? "active-tab" : ""
                      }`}
                      onClick={() => setActiveIssueTab("byMe")}
                    >
                      Reported Issues ({reported.total_issues_count || 0})
                    </button>
                  </li>
                </ul>

                {/* Sub-Tabs: Only show if the current category has issues */}
                {currentCategory.total_issues_count > 0 && (
                  <div className="p-3 pb-0 bg-light d-flex gap-2">
                    <button
                      className={`btn btn-sm rounded-pill px-3 ${
                        activeIssueSubTab === "active"
                          ? "btn-dark"
                          : "btn-white border text-muted"
                      }`}
                      onClick={() => setActiveIssueSubTab("active")}
                    >
                      Active ({currentCategory.active_issues_count || 0})
                    </button>
                    <button
                      className={`btn btn-sm rounded-pill px-3 ${
                        activeIssueSubTab === "closed"
                          ? "btn-dark"
                          : "btn-white border text-muted"
                      }`}
                      onClick={() => setActiveIssueSubTab("closed")}
                    >
                      Closed ({currentCategory.closed_issues_count || 0})
                    </button>
                  </div>
                )}

                <div className="p-3 bg-light">
                  {filteredIssues.length > 0 ? (
                    <div className="card border shadow-none mb-0 overflow-hidden">
                      <table className="table mb-0">
                        <thead className="bg-white">
                          <tr>
                            <th className="ps-3 text-center">Issue Id</th>
                            <th className="text-center">Description</th>
                            <th className="text-center">Date</th>
                            <th className="text-center">Status</th>
                            {activeIssueTab === "byMe" && (
                              <th className="text-center">Action</th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="bg-white">
                          {filteredIssues.map((issue) => (
                            <React.Fragment key={issue.issue_id}>
                              <tr
                                className="cursor-pointer align-middle"
                                onClick={() => toggleIssueItem(issue.issue_id)}
                              >
                                <td className="ps-3 py-3 text-center">
                                  <div
                                    className="copy-cell d-inline-flex align-items-center justify-content-center py-2"
                                    style={{ width: "fit-content" }}
                                  >
                                    {/* Tooltip to show the Full Issue ID on hover */}
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={
                                        <Tooltip
                                          id={`tooltip-issue-${issue.issue_id}`}
                                        >
                                          {issue.issue_id}
                                        </Tooltip>
                                      }
                                    >
                                      <a
                                        href="#"
                                        className="text-primary text-decoration-none fw-bold small"
                                        style={{
                                          cursor: "pointer",
                                          fontSize: "0.85rem",
                                        }}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          const path =
                                            activeIssueTab === "forMe"
                                              ? `/issues/buyer/${issue.issue_id}`
                                              : `/issues/seller/${issue.issue_id}`;
                                          navigate(path);
                                        }}
                                      >
                                        {issue.issue_id?.substring(0, 12)}...
                                      </a>
                                    </OverlayTrigger>

                                    {/* Copy Icon for the Full Issue ID */}
                                    <div className="ms-2">
                                      <CopyIcon textToCopy={issue.issue_id} />
                                    </div>
                                  </div>
                                </td>
                                <td
                                  className="text-truncate text-center"
                                  style={{ maxWidth: "200px" }}
                                >
                                  {issue.issue_description}
                                </td>
                                <td className="text-center">
                                  <div className="extra-small text-muted">
                                    {issue.issue_created_at}
                                  </div>
                                </td>
                                <td>
                                  <span
                                    className={` text-center badge ${
                                      issue.issue_status === "OPEN"
                                        ? "bg-danger-subtle text-danger"
                                        : issue.issue_status === "PROCESSING"
                                          ? "bg-warning-subtle text-warning"
                                          : "bg-success-subtle text-success"
                                    }`}
                                  >
                                    {issue.issue_status}
                                  </span>
                                  <div className="extra-small text-muted mt-1">
                                    Upd: {issue.issue_updated_at?.split(",")[0]}
                                  </div>
                                </td>
                                {activeIssueTab === "byMe" && (
                                  <td className="text-center">
                                    {expandedIssueId === issue.issue_id ? (
                                      <ChevronUp size={16} />
                                    ) : (
                                      <ChevronDown size={16} />
                                    )}
                                  </td>
                                )}
                              </tr>

                              {/* EXPANDABLE ROW */}
                              {expandedIssueId === issue.issue_id && (
                                <tr>
                                  <td colSpan="5" className="bg-light p-0">
                                    <div className="row g-0">
                                      {/* History Timeline */}
                                      <div className="col-md-6 border-end p-3">
                                        <h6 className="text-overline mb-3 text-muted">
                                          HISTORY
                                        </h6>
                                        <div className="history-timeline">
                                          {issue.issue_history &&
                                          issue.issue_history.length > 0 ? (
                                            issue.issue_history.map(
                                              (hist, hIdx) => (
                                                <div
                                                  key={hIdx}
                                                  className="mb-3 border-start ps-3 pb-2 position-relative"
                                                >
                                                  <div className="fw-bold small">
                                                    {hist.description
                                                      ?.short_desc ||
                                                      "Status Update"}
                                                  </div>
                                                  <div className="text-primary extra-small fw-bold">
                                                    {hist.code} —{" "}
                                                    <span className="text-muted fw-normal">
                                                      {hist.action_type}
                                                    </span>
                                                  </div>
                                                  <div className="text-muted extra-small mt-1">
                                                    {hist.updatedAt}
                                                  </div>
                                                </div>
                                              ),
                                            )
                                          ) : (
                                            <div className="text-muted small">
                                              No history available
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      <div className="col-md-6 p-3">
                                        <h6 className="text-overline mb-3 text-muted">
                                          PROPOSED RESOLUTIONS
                                        </h6>
                                        {issue.PendingResolutions &&
                                        issue.PendingResolutions.length > 0 ? (
                                          issue.PendingResolutions.map(
                                            (resGroup) => (
                                              <div
                                                key={resGroup.id}
                                                className="card p-3 border-0 shadow-sm mb-2"
                                              >
                                                <p className="extra-small text-muted mb-2">
                                                  Proposed by:{" "}
                                                  {
                                                    resGroup.parent_details
                                                      ?.proposed_by?.info
                                                      ?.person?.name
                                                  }
                                                </p>

                                                {resGroup.options?.map(
                                                  (option) => (
                                                    <div
                                                      className="form-check mb-2"
                                                      key={option.id}
                                                    >
                                                      <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name={`action-${issue.issue_id}`}
                                                        id={`opt-${option.id}`}
                                                        // ADDED: Logic to track selection
                                                        checked={
                                                          selectedResolutions[
                                                            issue.issue_id
                                                          ]
                                                            ?.selected_resolution_id ===
                                                          option.id
                                                        }
                                                        onChange={() =>
                                                          handleResolutionSelect(
                                                            issue.issue_id,
                                                            option.id,
                                                          )
                                                        }
                                                      />
                                                      <label
                                                        className="form-check-label small"
                                                        htmlFor={`opt-${option.id}`}
                                                      >
                                                        <strong>
                                                          {
                                                            option.description
                                                              ?.code
                                                          }
                                                        </strong>
                                                        :{" "}
                                                        {
                                                          option.description
                                                            ?.short_desc
                                                        }
                                                      </label>
                                                    </div>
                                                  ),
                                                )}

                                                <button
                                                  className="btn btn-primary w-100 btn-sm fw-bold mt-2"
                                                  // ADDED: Submit Handler
                                                  onClick={() =>
                                                    handleResolutionSubmit(
                                                      issue.issue_id,
                                                    )
                                                  }
                                                  disabled={
                                                    isResolutionSubmitting ||
                                                    !selectedResolutions[
                                                      issue.issue_id
                                                    ]
                                                  }
                                                >
                                                  {isResolutionSubmitting
                                                    ? "Submitting..."
                                                    : "Accept Resolution"}
                                                </button>
                                              </div>
                                            ),
                                          )
                                        ) : (
                                          <div className="card p-4 border-0 shadow-sm text-center">
                                            <div className="text-muted small mb-0">
                                              {issue.issue_status === "OPEN"
                                                ? "Waiting for counterparty to acknowledge..."
                                                : "No pending actions at this stage."}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-5 text-muted small border rounded bg-white">
                      No issues found in this category.
                    </div>
                  )}
                </div>
              </div>
            </div>
           )}
          </div>

          {/* --- RIGHT COLUMN: SIDEBAR --- */}
          <div className="col-lg-4">
            {/* 1. ORDER SUMMARY CARD */}
            <div className="card mb-4">
              <div
                className="card-header d-flex justify-content-between align-items-center cursor-pointer bg-white"
                onClick={() => toggleSection("summary")}
              >
                <div className="d-flex align-items-center gap-2">
                  <h6 className="mb-0 fw-bold text-dark">Order Summary</h6>
                </div>
                {showSections.summary ? (
                  <ChevronUp size={18} />
                ) : (
                  <ChevronDown size={18} />
                )}
              </div>
              {showSections.summary && (
                <div className="card-body">
                  {/* --- Standard Breakdown --- */}
                  <div className="summary-row">
                    <span className="text-muted">
                      Sub Total ({orderSummary.totalQuantity || 0} items)
                    </span>
                    <span className="text-dark">
                      ₹
                      {formatPrice(
                        orderData.orderFulfilments?.orderSummary
                          ?.totalOrderPrice,
                      )}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="text-muted">
                      Taxes ({orderSummary.totalQuantity || 0} items)
                    </span>
                    <span className="text-dark">₹0.00</span>
                  </div>
                  <div className="summary-row">
                    <span className="text-muted">
                      Delivery Charge ({orderSummary.totalQuantity || 0} items)
                    </span>
                    <span className="text-dark">
                      ₹{formatPrice(orderSummary.deliveryCharges)}
                    </span>
                  </div>

                  <div className="summary-row summary-strong">
                    <span className="text-dark">Total Order Amount</span>
                    <span className="text-dark">
                      ₹{formatPrice(orderData.order_amount)}
                    </span>
                  </div>

                  {/* --- Adjustments (Cancellations & Refunds) --- */}
                  {orderSummary.totalCancelPrice > 0 && (
                    <div className="summary-row text-danger">
                      <span>
                        Cancellations ({orderSummary.totalCancel || 0} items)
                      </span>
                      <span>
                        -₹{formatPrice(orderSummary.totalCancelPrice)}
                      </span>
                    </div>
                  )}
                  {
                    (orderData.order_amount - orderSummary.deliveryCharges) === orderSummary.totalCancelPrice && (
                       <div className="summary-row text-danger">
                    <span>
                      Delivery Charge ({orderSummary.totalQuantity || 0} items)
                    </span>
                    <span>
                      -₹{formatPrice(orderSummary.deliveryCharges)}
                    </span>
                  </div>
                    )
                  }

                  {orderSummary.totalReturnPrice > 0 && (
                    <div className="summary-row text-danger">
                      <span>
                        Refunds ({orderSummary.totalReturn || 0} items)
                      </span>
                      <span>
                        -₹{formatPrice(orderSummary.totalReturnPrice)}
                      </span>
                    </div>
                  )}

                  {orderSummary.totalPayable >= 0 && (
                    <div className="summary-row summary-strong">
                      <span className="text-dark">Net Order Total</span>
                      <span className="text-dark">
                        ₹{formatPrice(orderSummary.totalPayable)}
                      </span>
                    </div>
                  )}

                  {/* --- Fees & Commissions --- */}
                  <div className="summary-row text-danger">
                    <span>Buyer Finder Fee (Commission)</span>
                    <span>-₹{formatPrice(breakdown.finderFee?.amount)}</span>
                  </div>
                  <div className="summary-row text-danger">
                    <span>MSN Commission</span>
                    <span>-₹{formatPrice(breakdown.msnFee)}</span>
                  </div>

                  {/* --- Final Settlement --- */}
                  <div className="summary-footer-box mt-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="settlement-label">
                        Expected Settlement Amount
                      </span>
                      <span className="settlement-amount">
                        ₹{formatPrice(pricing.buyerPayable)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 2. ORDER HISTORY CARD */}
            <div className="card mb-4">
              <div
                className="card-header d-flex justify-content-between align-items-center cursor-pointer bg-white"
                onClick={() => toggleSection("history")}
              >
                <h6 className="mb-0 fw-bold text-dark">Order History</h6>
                {showSections.history ? (
                  <ChevronUp size={18} />
                ) : (
                  <ChevronDown size={18} />
                )}
              </div>

              {showSections.history && (
                <div className="card-body p-0">
                  {" "}
                  {/* Removed default padding to allow scrollbar to hug edges if preferred, padding is added inside scroll-area */}
                  <div className="history-scroll-area">
                    <div className="history-timeline">
                      {orderSummary.orderHistory?.map((log, index) => (
                        <div key={index} className="history-item">
                          {/* Header Row: Status Left, Date Right */}
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <div className="fw-bold text-dark small me-2">
                              {log.fulfilment_status}
                            </div>
                            <div className="extra-small text-muted text-end text-nowrap">
                              {log.action_update_time}
                            </div>
                          </div>

                          {/* Message Row: No Quotes */}
                          {log.message && (
                            <div className="text-muted extra-small fst-italic">
                              {log.message}
                            </div>
                          )}
                        </div>
                      )) || (
                        <div className="text-muted small py-3 text-center">
                          No history available.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 3. SHIPPING & BUYER DETAILS */}
            <div className="card mb-4">
              <div
                className="card-header d-flex justify-content-between align-items-center cursor-pointer bg-white"
                onClick={() => toggleSection("shipping")}
              >
                <h6 className="mb-0 fw-bold text-dark">Buyer & Shipping</h6>
                {showSections.shipping ? (
                  <ChevronUp size={18} />
                ) : (
                  <ChevronDown size={18} />
                )}
              </div>
              {showSections.shipping && (
                <div className="card-body small">
                  <div className="mb-4">
                    <div className="text-overline mb-2">CUSTOMER DETAILS</div>
                    <div className="fw-bold text-dark mb-1">
                      {orderData.billing_data?.name}
                    </div>
                    <div className="text-muted mb-1">
                      {orderData.billing_data?.phone}
                    </div>
                    <div className="text-muted">
                      {orderData.billing_data?.email}
                    </div>
                  </div>
                  <div className="pt-3 border-top">
                    <div className="text-overline mb-2">SHIPPING ADDRESS</div>
                    <div className="text-dark lh-sm">
                      {orderData.shipping_data?.location?.address?.building},
                      <br />
                      {orderData.shipping_data?.location?.address?.locality},
                      <br />
                      {orderData.shipping_data?.location?.address?.city},{" "}
                      {orderData.shipping_data?.location?.address?.state}
                      <br />
                      <span className="fw-semibold">
                        PIN:{" "}
                        {orderData.shipping_data?.location?.address?.area_code}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Modal
        show={isRaiseModalOpen}
        onHide={closeRaiseModal}
        centered
        size="xl"
      >
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="fw-bold fs-5">Raise a New Issue</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          <form>
            {/* IDENTIFICATION ROW */}
            <div className="row mb-3">
              {/* Inside the Raise Issue Modal in OrderDetails.jsx */}

              {/* Inside the Raise Issue Modal in OrderDetails.jsx */}

              <div className="col-md-4">
                <label className="form-label small fw-bold">
                  Domain <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control bg-light"
                  value={orderData?.domain || ""}
                  readOnly
                  disabled
                  title={`Domain Code: ${raiseIssueFormData.domain}`}
                />
                <div className="extra-small text-muted mt-1">
                  Current Order Domain: {orderData.domain}
                </div>
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-bold">
                  Category <span className="text-danger">*</span>
                </label>
                <select
                  id="issue_category"
                  className="form-select"
                  value={raiseIssueFormData.issue_category}
                  onChange={handleCategoryChange}
                >
                  <option value="">Select Category</option>
                  {categoryMapping?.map((c) => (
                    <option key={c.code} value={c.category}>
                      {c.category}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-bold">
                  Subcategory <span className="text-danger">*</span>
                </label>
                <select
                  id="issue_subcategory"
                  className="form-select"
                  value={raiseIssueFormData.issue_subcategory}
                  onChange={handleFormChange}
                  disabled={!raiseIssueFormData.issue_category}
                >
                  <option value="">Select Subcategory</option>
                  {subCategoryOptions.map((sc) => (
                    <option
                      key={sc.code}
                      value={sc.sub_category || sc.subb_category}
                    >
                      {sc.sub_category || sc.subb_category}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* BAP & CITY ROW */}
            <div className="row mb-3">
              <div className="col-md-8">
                <label className="form-label small fw-bold text-secondary">
                  BAP ID <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control bg-light"
                  value={raiseIssueFormData.bap_id}
                  readOnly
                />
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-bold text-secondary">
                  City STD Code<span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control city-std-input"
                  id="city"
                  placeholder="std:0120"
                  value={raiseIssueFormData.city}
                  onChange={handleFormChange}
                  required
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label small fw-bold text-secondary">
                BAP URI <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control bg-light"
                value={raiseIssueFormData.bap_uri}
                readOnly
              />
            </div>

            <hr className="my-4 opacity-10" />

            {/* REFERENCES SECTION (DYNAMIC) */}
            <div className="mb-3">
              <label className="form-label fw-bold small">
                References <span className="text-danger">*</span>
              </label>
              {raiseIssueFormData.refs.map((ref, index) => (
                <div className="row g-2 mb-2 align-items-end" key={index}>
                  <div className="col-md-5">
                    <label className="extra-small text-muted mb-1">
                      Reference Type
                    </label>
                    <select
                      className="form-select form-select-sm"
                      value={ref.ref_type}
                      onChange={(e) =>
                        handleRefChange(index, "ref_type", e.target.value)
                      }
                      disabled={index === 0 && ref.ref_type === "ORDER" }
                    >
                      <option value="">Select Type</option>
                      {referenceType?.map((refType) => (
                        <option key={refType} value={refType}>
                          {formatLabel(refType)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-5">
                    <label className="extra-small text-muted mb-1">
                      Reference ID
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="e.g., order-123"
                      value={ref.ref_id}
                      onChange={(e) =>
                        handleRefChange(index, "ref_id", e.target.value)
                      }
                    />
                  </div>
                  <div className="col-md-2">
                    {raiseIssueFormData.refs.length > 1 && (
                      <Button
                        variant="outline-danger"
                        className="border-0 mb-1"
                        size="sm"
                        onClick={() => removeRefRow(index)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              <Button
                variant="link"
                className="p-0 text-decoration-none extra-small"
                onClick={addRefRow}
              >
                <Plus size={14} className="me-1" /> Add Another Reference
              </Button>
            </div>

            <hr className="my-4 opacity-10" />

            {/* DESCRIPTION & IMAGES */}
            <div className="mb-3">
              <label className="form-label small fw-bold">
                Short Description <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="short_desc"
                className="form-control"
                value={raiseIssueFormData.short_desc}
                onChange={handleFormChange}
                placeholder="A brief summary of the issue"
              />
            </div>

            <div className="mb-3">
              <label className="form-label small fw-bold">
                Detailed Description
              </label>
              <textarea
                id="detailed_desc"
                className="form-control"
                rows={3}
                value={raiseIssueFormData.detailed_desc}
                onChange={handleFormChange}
                placeholder="Provide more details, steps to reproduce, etc."
              />
            </div>

            <div className="mb-2">
              <label className="form-label small fw-bold">
                Images (Multi-select)
              </label>
              <input
                type="file"
                className="form-control"
                multiple
                onChange={handleFileChange}
              />
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer className="border-top-0 pt-0">
          <Button
            variant="secondary"
            onClick={closeRaiseModal}
            className="px-4 border-0"
            style={{ backgroundColor: "#9ca3af", fontSize: "13px" }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleRaiseIssueSubmit}
            disabled={isSubmitting}
            className="px-4 border-0"
            style={{ backgroundColor: "#3498db", fontSize: "13px" }}
          >
            {isSubmitting ? "Raising..." : "Raise Issue"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OrderDetails;
